import React, { useEffect, useMemo, useState } from "react";
import { Card, CardHeader, CardContent, Stack, TextField, MenuItem, IconButton, Button, Box, FormControlLabel, Checkbox, Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from '@mui/icons-material/Delete';
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { getBoxList, GoodsInfo } from "adoms-common-lib";

export type DeliveryItem = {
    id: string;
    name: string;
    packageType: string; // e.g., 'ボックス'
};

export type DeliveryItemInputProps = {
    title?: string; // default: '配送物の情報'
    items?: GoodsInfo[]; // controlled mode if provided
    onChange?: (items: GoodsInfo[]) => void;
    typeOptions?: string[]; // default: ['ボックス', '封筒', 'その他']
    className?: string;
    style?: React.CSSProperties;
};

const generateId = () => {
    // Prefer crypto.randomUUID when available
    // Fallback to a simple unique string
    // @ts-ignore
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

const createGoodsItem: (defaults?: Partial<GoodsInfo>) => GoodsInfo = (defaults) => ({
    weight: 0,
    name: "",
    count: 1,
    id: generateId(),
    handlingInstructions: [],
    packageType: "指定なし"
});

export const DeliveryItemInput: React.FC<DeliveryItemInputProps> = ({
    title = "配送物の情報",
    items,
    onChange,
    typeOptions,
    className,
    style,
}) => {
    const options = useMemo(() => typeOptions ?? ["指定なし"].concat(getBoxList().map(box => box.boxName)), [typeOptions]);
    const [goodsItems, setGoodsItemsState] = useState<GoodsInfo[]>(
        (items && items.length ? items : [createGoodsItem()]).map(item => ({
            ...item,
            handlingInstructions: item.handlingInstructions ?? ["指定なし"]
        }))
    );
    const [isAddButtonDisabled, setIsAddButtonDisabled] = useState((items ?? []).length >= 2);

    // Sync when controlled
    useEffect(() => {
        if (!items) return;
        setGoodsItemsState(
            (items.length ? items : [createGoodsItem()]).map(item => ({
                ...item,
                handlingInstructions: item.handlingInstructions ?? []
            }))
        );
    }, [items]);

    const updateItems = (nextItems: GoodsInfo[]) => {
        setGoodsItemsState(nextItems);
        onChange?.(nextItems);
    };

    const handleAdd = () => {
        if (goodsItems.length >= 1) {
            setIsAddButtonDisabled(true);
        }
        updateItems([...goodsItems, createGoodsItem()]);
    };

    const handleRemove = (goodsId: string) => {
        const nextItems = goodsItems.filter((x) => x.id !== goodsId);
        if (nextItems.length < 2) setIsAddButtonDisabled(false);
        updateItems(nextItems.length ? nextItems : [createGoodsItem()]);
    };

    const handleNameChange = (goodsId: string, name: string) => {
        updateItems(goodsItems.map((x) => (x.id === goodsId ? { ...x, name } : x)));
    };

    const handleTypeChange = (goodsId: string, packageType: string) => {
        let changeHandlingInstructions: string[] = [];
        if (packageType !== "指定なし") {
            const box = getBoxList().find(box => box.boxName === packageType);
            if (box) {
                changeHandlingInstructions.push("室温1-30℃");
            }
        }
        updateItems(goodsItems.map((x) => (x.id === goodsId ? { ...x, packageType, handlingInstructions: changeHandlingInstructions } : x)));
    };

    const handleTempratuereChange = (goodsId: string, handlingInstructions: string) => {
        updateItems(goodsItems.map((x) => (x.id === goodsId ? { ...x, handlingInstructions: [handlingInstructions] } : x)));
    }

    const displayTempratureMenu = (item: GoodsInfo) => {
        const box = getBoxList().find(box => box.boxName === item.packageType);
        
        if (!box) {
            return (
                <TextField
                    select
                    value={item.handlingInstructions?.length && item.handlingInstructions?.length !== 0 ? item.handlingInstructions[0] : "指定なし"}
                    onChange={(e) => handleTempratuereChange(item.id!!, e.target.value)}
                    size="small"
                    sx={{ minWidth: 180, flexShrink: 0 }}
                >
                    <MenuItem key={'指定なし'} value={'指定なし'}>
                        指定なし
                    </MenuItem>
                </TextField>
            );
        }

        return (
            <TextField
                select
                value={item.handlingInstructions?.length && item.handlingInstructions?.length !== 0 ? item.handlingInstructions[0] : "室温1-30℃"}
                onChange={(e) => handleTempratuereChange(item.id!!, e.target.value)}
                size="small"
                sx={{ minWidth: 180, flexShrink: 0 }}
            >
                <MenuItem key={'室温1-30℃'} value={'室温1-30℃'}>
                    室温1-30℃
                </MenuItem>
                {box.refrigeratedWeight && [
                    <MenuItem key={'保冷（2-5℃（医薬品））'} value={'保冷（2-5℃（医薬品））'}>
                        保冷（2-5℃（医薬品））
                    </MenuItem>,
                    <MenuItem key={'2-6℃（血液）'} value={'2-6℃（血液）'}>
                        2-6℃（血液）
                    </MenuItem>
                ]}
                {box.frozenWeight && (
                    <MenuItem key={'-20℃以下（血液）'} value={'-20℃以下（血液）'}>
                        -20℃以下（血液）
                    </MenuItem>
                )}
            </TextField>
        );
    };

    const defaultComponent = () => {
        return (
            <Card className={className} style={style} variant="outlined" square sx={{ borderRadius: 0 }}>
                <CardContent sx={{ paddingTop: 2, paddingBottom: 1.5, paddingX: { xs: 2, sm: 3 } }}>
                    <Box>
                        {goodsItems.map((item, index) => {
                            const isFirst = index === 0;
                            const isLast = index === goodsItems.length - 1;
                            return (
                                <Stack
                                    key={item.id}
                                    direction="row"
                                    spacing={1.5}
                                    alignItems="flex-start"
                                    justifyContent="center"
                                    sx={{ marginBottom: 1.5 }}
                                >
                                    <Stack direction="column" spacing={1}>
                                        {isFirst ? <Box sx={{ textAlign: 'light' }}>物品名</Box> : null}
                                        <TextField
                                            value={item.name}
                                            onChange={(e) => handleNameChange(item.id!!, e.target.value)}
                                            size="small"
                                            sx={{ minWidth: 180 }}
                                            fullWidth
                                        />
                                        {isLast && (
                                            <Button
                                                disabled={isAddButtonDisabled}
                                                variant="text"
                                                startIcon={<AddIcon />}
                                                onClick={handleAdd}
                                                sx={{ alignSelf: 'flex-start' }}
                                            >
                                                追加
                                            </Button>
                                        )}
                                    </Stack>
                                    <Stack direction="column" spacing={1}>
                                        {isFirst ? <Box sx={{ textAlign: 'center' }}>梱包タイプ</Box> : null}
                                        <TextField
                                            select
                                            value={item.packageType}
                                            onChange={(e) => handleTypeChange(item.id!!, e.target.value)}
                                            size="small"
                                            sx={{ minWidth: 180, flexShrink: 0 }}
                                        >
                                            {options.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                    </Stack>
                                    <Stack direction="column" spacing={1}>
                                        {isFirst ? <Box sx={{ minWidth: 42, textAlign: 'center', flexShrink: 0 }}>温度帯</Box> : null}
                                        {displayTempratureMenu(item)}
                                    </Stack>
                                    <Stack direction="column" spacing={1}>
                                        {isFirst ? <Box sx={{ minWidth: 42, textAlign: 'center' }}>&nbsp;</Box> : null}
                                        <IconButton aria-label="削除" title="削除" onClick={() => handleRemove(item.id!!)} sx={{ flexShrink: 0 }}>
                                            <DeleteOutlineIcon />
                                        </IconButton>
                                    </Stack>
                                </Stack>
                            );
                        })}
                    </Box>
                </CardContent>
            </Card>
        )
    }

    const smartphoneComponent = () => {
        return (
            <Grid container spacing={2} sx={{ paddingTop: 1, paddingBottom: 1, paddingX: 2 }}>
                {goodsItems.map((item) => {
                    const isRefrigeratedFlag = (item.handlingInstructions ?? []).includes("保冷");
                    return (
                        <Grid item xs={12} key={item.id}>
                            <Card>
                                <CardContent>
                                    <Stack direction="column" spacing={2} alignItems="flex-start" justifyContent="center" sx={{ marginBottom: 1.5 }}>
                                        <Button
                                            variant="outlined"
                                            startIcon={<DeleteIcon />}
                                            sx={{ alignSelf: 'flex-end' }}
                                            onClick={() => handleRemove(item.id!!)}
                                        >
                                            削除
                                        </Button>
                                        <TextField
                                            value={item.name}
                                            onChange={(e) => handleNameChange(item.id!!, e.target.value)}
                                            size="small"
                                            label="物品名"
                                            fullWidth
                                        />
                                        <TextField
                                            select
                                            value={item.packageType}
                                            onChange={(e) => handleTypeChange(item.id!!, e.target.value)}
                                            size="small"
                                            label="梱包タイプ"
                                            fullWidth
                                        >
                                            {options.map((option) => (
                                                <MenuItem key={option} value={option}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </TextField>
                                        {displayTempratureMenu(item)}
                                    </Stack>
                                </CardContent>
                            </Card>
                        </Grid>
                    )
                })}
                {/* 追加ボタン（スマホ用） */}
                <Grid item xs={12}>
                    <Button
                        disabled={isAddButtonDisabled}
                        onClick={handleAdd}
                        variant="contained"
                        startIcon={<AddIcon />}
                        fullWidth
                    >
                        追加
                    </Button>
                </Grid>
            </Grid>
        )
    }

    return (
        //xs表示はスマホ用、sm以上はPC用
        <Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {defaultComponent()}
            </Box>
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
                {smartphoneComponent()}
            </Box>
        </Box>
    );
};